// src/features/phase05/EditADLRecord.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import ADLRecordForm from "./ADLRecordForm";
import {
  fetchAdlRecord,
  updateAdlRecord,
  fetchAdlActivities,
  clearCurrentAdlRecord,
  clearError,
} from "../../../store/slices/adlRecordsSlice";

const EditADLRecord = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};


  const { currentAdlRecord, adlActivities, isLoading, error } = useSelector(
    (state) => state.adlRecords
  );
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    clientName: "",
    roomNumber: "",
    dateRange: "",
    checkedBy: "",
    nursingManager: "",
    activities: [],
  });
  const [errors, setErrors] = useState({});

  // Fetch data
  useEffect(() => {
    dispatch(clearCurrentAdlRecord());
    dispatch(fetchAdlRecord(id));
    dispatch(fetchAdlActivities());
  }, [dispatch, id]);

  // Populate form when record loads
  useEffect(() => {
    if (currentAdlRecord?.record) {
      const rec = currentAdlRecord.record;

      // Format date for <input type="date">
      const formatDate = (iso) =>
        iso ? iso.split("T")[0] : "";

      // Normalize activity records
      const normalizedActivities = (rec.activity_records || rec.activityRecords || []).map(
        (act) => ({
          activity_id: act.activity_id,
          date: formatDate(act.date),
          score: String(act.score), // radio expects string
          totalScore: act.total_score || "",
          staffName: act.staff_name || act.staffName || "",
          created_at: act.created_at || "",
          id: act.id || 0,
        })
      );

      setFormData({
        patientId: patientId || rec.patientId,
        caseId: caseId || rec.caseId,
        clientName: rec.client_name || "",
        roomNumber: rec.room_number || "",
        dateRange: rec.date_range || "",
        checkedBy: rec.checked_by || "",
        nursingManager: rec.nursing_manager || "",
        activities: normalizedActivities,
      });
    }
  }, [currentAdlRecord]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearCurrentAdlRecord()); // Yeh lazmi add karein
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleActivitiesChange = React.useCallback((activitiesData) => {
    setFormData(prev => {
      // Sirf tab update karein agar data waqai different ho
      if (JSON.stringify(prev.activities) === JSON.stringify(activitiesData)) {
        return prev;
      }
      return {
        ...prev,
        activities: activitiesData
      };
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.clientName?.trim()) newErrors.clientName = "Client name is required.";
    if (!formData.roomNumber?.trim()) newErrors.roomNumber = "Room number is required.";
    if (!formData.dateRange?.trim()) newErrors.dateRange = "Date range is required.";
    if (formData.activities.length === 0) newErrors.activities = "At least one activity is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire("Missing Fields", "Please fill all required fields.", "warning");
      return;
    }

    const payload = {
      patient_id: formData.patientId,
      case_id: formData.caseId,
      client_name: formData.clientName,
      room_number: formData.roomNumber,
      date_range: formData.dateRange,
      checked_by: formData.checkedBy,
      nursing_manager: formData.nursingManager,
      activities: formData.activities.map((a) => ({
        activity_id: a.activity_id,
        date: a.date,
        score: Number(a.score), // backend expects number
        total_score: a.totalScore,
        staff_name: a.staffName,
      })),
    };
    // console.log("Case id: ", formData.caseId);
    // console.log("Patient id: ", formData.patientId);

    try {
      await dispatch(updateAdlRecord({ case_id: caseId, adlRecordData: payload })).unwrap();

      Swal.fire({
        title: "Updated!",
        text: "ADL record updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Stay on edit page
    } catch (err) {
      const errorMessage =
        typeof err === "object" && err !== null
          ? Object.values(err)[0]?.[0] || "Update failed"
          : err;

      Swal.fire("Error!", errorMessage, "error");
    }
  };

  if (isLoading || !currentAdlRecord?.record || adlActivities.length === 0) {
    return (
      <MainLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading activities and record...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        {/* Top Bar: Breadcrumbs + Print Button */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Breadcrumbs />
          <Link
            to={`/phase05/adl-record-sheet/print/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            <i className="ri-printer-line me-1"></i> Print
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <ADLRecordForm
                formData={formData}
                handleChange={handleChange}
                handleActivitiesChange={handleActivitiesChange}
                adlActivities={adlActivities}
                isLoading={isLoading}
                errors={errors}
                isEdit={true}
                initialActivities={formData.activities}
              />
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditADLRecord;